<template>
  <b-container fluid class="no-bottom">
    <backlink />
    <h1>
      <div class="indicator">
        {{ items.length }}
        <i class="far fa-long-arrow-left fa-xs"></i>
      </div>
      Réceptions <br />en attente
    </h1>

    <b-form-group inline>
      <b-form-select v-model="typeFilter" :options="typeOptions" class="mb-2" />
      <b-input-group>
        <b-form-input v-model="filter" placeholder="Rechercher un numéro" type="text" />
        <b-input-group-append v-if="filter">
          <b-button :disabled="!filter" @click="filter = ''" variant="outline-secondary">
            <i class="far fa-times"></i>
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <div class="table-wrapper">
      <b-table ref="table" hover show-empty :fields="fields" :items="items" :busy="isBusy" :filter="filter"
        :filter-function="filterFunction">
        <template slot="table-busy">
          <div class="text-center my-2">
            <div v-if="isBusy" class="loader"></div>
            Les données sont en cours de chargement...
          </div>
        </template>
        <template slot="empty" slot-scope="scope">
          <div class="text-center mt-2">Cette liste est vide pour le moment</div>
        </template>

        <template slot="number" slot-scope="data">
          <router-link v-if="canOpen(data.item)" :to="detailRoute(data.item)">{{ data.value }}</router-link>
          <template v-else>{{ data.value }}</template>
          <div class="text-muted">{{ data.item.origin }}</div>
        </template>
        <template slot="type" slot-scope="data">
          <b-badge :variant="data.value == 'transfer' ? 'info' : 'primary'">
            {{ data.value == "transfer" ? "Transfert" : "Retour client" }}
          </b-badge>
        </template>
      </b-table>
    </div>
  </b-container>
</template>

<script>
export default {
  name: "ReceptionMobileList",
  data() {
    return {
      fields: [
        {
          key: "number",
          label: "Numéro",
        },
        {
          key: "date",
          label: "Date",
          formatter: "date",
        },
        {
          key: "type",
          label: "Type",
        },
      ],
      typeOptions: [
        { value: null, text: "— Tous les types —" },
        { value: "return", text: "Retours clients" },
        { value: "transfer", text: "Transferts inter-entrepôts" },
      ],
      typeFilter: null,
      filter: null,
      isBusy: false,
      transfers: [],
    };
  },
  computed: {
    /**
     * Retours clients en attente, filtrés sur les groupes dont l'utilisateur est préparateur.
     */
    businessReturns() {
      return this.$store.getters["business_return/getByStatusSlug"](
        "business-return-transport-en-cours"
      ).filter((item) => {
        if (
          this.$root.isAuthorized("business-return_im-preparator") &&
          item.group_slugs &&
          item.group_slugs.includes("instruments")
        ) {
          return true;
        }
        if (
          this.$root.isAuthorized("business-return_ot-preparator") &&
          item.group_slugs &&
          item.group_slugs.includes("outillage")
        ) {
          return true;
        }
        if (
          this.$root.isAuthorized("business-return_snl-preparator") &&
          item.group_slugs &&
          item.group_slugs.includes("service_et_logistique")
        ) {
          return true;
        }
        return false;
      });
    },
    /**
     * Liste unifiée retours clients + transferts inter-entrepôts entrants.
     */
    items() {
      let items = [];

      if (this.typeFilter !== "transfer") {
        this.businessReturns.forEach((item) => {
          items.push({
            type: "return",
            id: item.id,
            number: item.return_number,
            date: item.withdraw_wished_date,
            origin:
              item.material_retrieving_place == "axiloc"
                ? "Retour à l'entrepôt ESM"
                : "Enlèvement sur site",
          });
        });
      }

      if (this.typeFilter !== "return") {
        this.transfers.forEach((item) => {
          items.push({
            type: "transfer",
            id: item.id,
            number: item.transfer_number,
            date: item.created_at,
            origin:
              "Depuis " +
              (item.source_warehouse ? item.source_warehouse.name : "—") +
              " · " +
              item.lines_count +
              " ligne(s)",
          });
        });
      }

      return items;
    },
  },
  watch: {
    // Le type sélectionné est reflété dans l'URL (partageable / conservé au rafraîchissement).
    typeFilter(value) {
      const query = Object.assign({}, this.$route.query);
      if (value) {
        query.type = value;
      } else {
        delete query.type;
      }

      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ name: "ReceptionMobileList", query: query });
      }
    },
    "$route.query.type": function (value) {
      this.typeFilter = value || null;
    },
  },
  mounted() {
    const type = this.$route.query.type;
    if (type == "return" || type == "transfer") {
      this.typeFilter = type;
    }

    this.isBusy = !this.items.length;

    this.$store
      .dispatch("business_return/getByStatusSlug", ["business-return-transport-en-cours"])
      .then(() => (this.isBusy = false));

    this.loadTransfers();
  },
  methods: {
    date(value) {
      // Les transferts arrivent déjà formatés par l'API (d/m/Y H:i), les retours en date ISO.
      if (!value) {
        return null;
      }
      return value.indexOf("/") !== -1 ? value : this.$moment(value).format("DD-MM-YYYY");
    },
    /**
     * Transferts entrants encore en transit. Un utilisateur rattaché à un entrepôt
     * ne voit que les transferts à destination de son entrepôt.
     */
    loadTransfers() {
      const warehouse = this.$store.getters["auth/warehouse"];
      const params = {
        status: "warehouse-transfer-envoye,warehouse-transfer-receptionne-partiellement",
      };
      if (warehouse && warehouse.id) {
        params.target = warehouse.id;
      }

      this.$http
        .get(process.env.BACK_API_URL + "warehouse-transfer", { params })
        .then((response) => {
          this.transfers = (response.data && response.data.transfers) || [];
          this.isBusy = false;
        })
        .catch(() => {
          this.$toastr.error("Les transferts inter-entrepôts n'ont pas pu être chargés.");
          this.isBusy = false;
        });
    },
    canOpen(item) {
      return item.type == "transfer"
        ? true
        : this.$store.getters["auth/isAuthorized"]("business-return_show");
    },
    detailRoute(item) {
      return item.type == "transfer"
        ? { name: "WarehouseTransferMobileDetail", params: { id: item.id } }
        : { name: "BusinessReturnMobileDetail", params: { id: item.id } };
    },
    /**
     * Fonction de filtrage utilisée par le champ de recherche (numéro de demande / de transfert).
     * @param   {object}    row     Objet de la ligne à vérifier
     * @param   {string}    filter  Mots clés saisis dans le champ de recherche
     * @returns {boolean}           True si la ligne testée correspond à la recherche, sinon false
     */
    filterFunction(row, filter) {
      const regex = new RegExp(filter.toLowerCase().replace(/\s/g, ".*"), "gi");

      return row.number ? row.number.toString().toLowerCase().match(regex) !== null : false;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/ReceptionMobile/ReceptionMobileList.vue